import {
  queueAccountKey,
  QueueHttpError,
  type QueueAccount,
} from "../shopify-queue/client.ts";
export type ComparisonAction =
  | { action: "start"; preview_hash: string; connection_id?: string }
  | { action: "retry" | "cancel"; run_id: string; revision: number };
type Saved = { id: string; body: string };
type State = {
  pending: Saved | null;
  busy: boolean;
  error: string;
  success: string;
};
type Storage = Pick<
  globalThis.Storage,
  "length" | "key" | "getItem" | "setItem" | "removeItem"
>;
export async function comparisonRequest<T>(
  base: string,
  account: QueueAccount,
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set("Content-Type", "application/json");
  headers.set("X-Shop-Domain", account.domain);
  if (account.organizationId)
    headers.set("X-Organization-Id", account.organizationId);
  const res = await fetch(
    base + "/api/sync/stock-comparisons" + path.replace(/^\/(?=\?|$)/, ""),
    { ...options, headers, credentials: "include", cache: "no-store" },
  );
  const data = await res.json().catch(() => null);
  if (!res.ok || !data || data.success !== true)
    throw new QueueHttpError(
      data?.error || "The response could not be confirmed",
      res.ok ? 500 : res.status,
    );
  return data as T;
}
export function createComparisonCommands(
  base: string,
  account: QueueAccount,
  storage: Storage,
) {
  const prefix = "whenly-stock-comparison-v1:" + queueAccountKey(account) + ":";
  const restore = () => {
    const keys = Array.from({ length: storage.length }, (_, i) =>
      storage.key(i),
    )
      .filter((v): v is string => !!v && v.startsWith(prefix))
      .sort();
    for (const key of keys) {
      const raw = storage.getItem(key);
      if (!raw) continue;
      const saved = JSON.parse(raw) as Saved,
        body = JSON.parse(saved.body);
      if (
        key !== prefix + saved.id ||
        body.request_id !== saved.id ||
        body.expected_user_id !== account.userId
      )
        throw new Error("Saved stock comparison is invalid");
      return saved;
    }
    return null;
  };
  let state: State = { pending: null, busy: false, error: "", success: "" };
  try {
    state.pending = restore();
  } catch {
    state.error =
      "Saved stock comparisons could not be read. Browser storage must be available.";
  }
  const listeners = new Set<() => void>(),
    update = (values: Partial<State>) => {
      state = { ...state, ...values };
      listeners.forEach((fn) => fn());
    };
  const retry = async () => {
    if (state.busy || !state.pending) return;
    const pending = state.pending,
      command = JSON.parse(pending.body);
    update({ busy: true, error: "", success: "" });
    try {
      const result = await comparisonRequest<{
        request_id: string;
        revision: number;
        action: string;
        run_id: string;
      }>(base, account, "/commands", { method: "POST", body: pending.body });
      if (
        result.request_id !== pending.id ||
        result.action !== command.action ||
        result.run_id !==
          (command.action === "start" ? pending.id : command.run_id) ||
        result.revision !==
          (command.action === "start" ? 1 : command.revision + 1)
      )
        throw new QueueHttpError(
          "The saved receipt did not match. Retry the same action.",
          500,
        );
      storage.removeItem(prefix + pending.id);
      update({
        pending: restore(),
        success:
          "Stock comparison action saved. Stock quantities were not changed.",
      });
    } catch (error) {
      if (
        error instanceof QueueHttpError &&
        [400, 409, 422].includes(error.status)
      ) {
        try {
          storage.removeItem(prefix + pending.id);
          update({ pending: restore() });
        } catch {
          /* Preserve uncertain local receipt. */
        }
      }
      update({
        error:
          error instanceof Error
            ? error.message
            : "The outcome is unknown. Retry the saved action.",
      });
    } finally {
      update({ busy: false });
    }
  };
  return {
    snapshot: () => state,
    subscribe: (fn: () => void) => {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    retry,
    submit: async (action: ComparisonAction) => {
      if (state.pending || state.busy) return;
      const id = crypto.randomUUID(),
        saved = {
          id,
          body: JSON.stringify({
            ...action,
            request_id: id,
            expected_user_id: account.userId,
          }),
        };
      try {
        const previous = restore();
        if (previous) {
          update({
            pending: previous,
            error: "Confirm the saved action first.",
          });
          return;
        }
        storage.setItem(prefix + id, JSON.stringify(saved));
      } catch {
        update({
          error:
            "The browser could not save the action. Allow browser storage before trying again.",
        });
        return;
      }
      update({ pending: saved, error: "", success: "" });
      await retry();
    },
  };
}
